import * as React from 'react';
import {
    Link,
    useLocation,
    useParams,
    useNavigate,
} from 'react-router-dom';

// Type definitions for compatibility
export interface MatchRenderProps<T = {}> {
  path: string;
  url: string;
  uri?: string; // @reach/router used 'uri' instead of 'url' in some contexts
  isExact: boolean;
  params: T;
  // Flatten common route parameters for compatibility
  activeChannelId?: string;
  activeEventId?: string;
  activeObject?: string;
  activeStoryboardUrl?: string;
  activeStoryUrl?: string;
  activeStoryboardHashid?: string;
  activeStoryObject?: string;
  teamId?: string;
  agentId?: string;
  customerId?: string;
}

export type MatchRenderFn<T = {}> = (props: { match: MatchRenderProps<T> | null }) => React.ReactNode;

export interface RouterProps {
  children?: React.ReactNode;
  primary?: boolean;
  component?: React.ComponentType<any>;
}

// Re-export React Router v6 components
export {
    Link,
    useLocation,
    useParams,
    useNavigate,
};

// Export navigate as a wrapper around useNavigate for compatibility
export const navigate = (path: string) => {
    // This is a compatibility function - in real usage, components should use useNavigate hook
    // For now, we'll use window.history.pushState for client-side navigation
    if (path.startsWith('http') || path.startsWith('//')) {
        // External URLs still need full redirect
        window.location.href = path;
    } else {
        // Internal navigation - use history API
        window.history.pushState({}, '', path);
        // Trigger a popstate event to notify React Router
        window.dispatchEvent(new PopStateEvent('popstate'));
    }
};

// Mock redirectTo function for compatibility
export const redirectTo = (path: string) => {
    console.warn('redirectTo from @reach/router compatibility shim is deprecated, use navigate instead');
    navigate(path);
};

// Mock Match component for compatibility
export const Match = ({path, children}: { path: string; children: (props: { match: any }) => React.ReactNode }) => {
    const location = useLocation();
    const match = location.pathname.includes(path) ? {path, url: location.pathname, isExact: false, params: {}} : null;
    return children({match});
};

// Mock Router export (should not be used in new code)
export const Router = ({children, primary, component}: RouterProps) => {
    console.warn('Router from @reach/router compatibility shim is deprecated');
    if (component) {
        return React.createElement(component, {children});
    }
    return React.createElement(React.Fragment, null, children);
};
